import { useQuery } from '@tanstack/react-query';
import {
  MemberGroupByIdQuery,
  MemberGroupByIdQueryVariables,
  MemberGroupFilter,
  MemberGroupsQuery,
  MemberGroupsQueryVariables,
} from '__generated__/graphql';
import classNames from 'classnames';
import GroupPicker, { IGroupPickerProps } from 'components/domain/pickers/GroupPicker/GroupPicker';
import { useActiveMember, useOpenable } from 'hooks';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useActivePosition from 'hooks/models/position/useActivePosition';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { useReactQueryLazyLoading } from 'hooks/react-query/useReactQueryLazyLoading/useReactQueryLazyLoading';
import useScrollerRef from 'hooks/ui/useScrollerRef';
import { ClassName } from 'types/ClassName';
import IMemberGroup from 'types/MemberGroup';
import { NonNullArrayField } from 'types/util/NonNullArrayField';
import { getGraphQlEqualComparison } from 'utils/apolloUtils';
import { memberGroupQuery, memberGroupsQuery } from './employee-group-picker-queries';
import { executeFunctionsCurried } from 'utils/functionUtils';

export interface IEmployeeGroupPickerProps
  extends Omit<IGroupPickerProps, 'data' | 'isLoading' | 'currentGroup' | 'dataError' | 'loadedAll'> {
  filterByPermission?: boolean;
  className?: ClassName;
  excludeIds?: IMemberGroup['id'][];
}

type EmployeeGroupPickerGroup = NonNullArrayField<MemberGroupsQuery['memberGroups']>;

function EmployeeGroupPicker({
  filterByPermission = false,
  excludeIds,
  value,
  className,
  onOpen,
  onClose,
  ...pickerProps
}: IEmployeeGroupPickerProps) {
  const activeMember = useActiveMember();
  const activePosition = useActivePosition();
  const graphqlClient = useGraphQLClient();

  const toggle = useOpenable();
  const { scroller, setScrollerRef } = useScrollerRef();

  const sectionSize = 20;
  const baseKey = useReactQueryBaseKey();

  const { data: selectedGroup } = useQuery({
    queryKey: [...baseKey, 'member-group-picker-by-id', value],
    queryFn: async () => {
      if (!value) {
        return null;
      }

      const result = await graphqlClient.request<MemberGroupByIdQuery, MemberGroupByIdQueryVariables>({
        document: memberGroupQuery,
        variables: { groupId: value },
      });

      return result.memberGroups?.[0]?.groupName ?? null;
    },
    enabled: !!value,
  });

  async function getMemberGroups(after: string | null, first: number): Promise<EmployeeGroupPickerGroup[]> {
    const result = await graphqlClient.request<MemberGroupsQuery, MemberGroupsQueryVariables>({
      document: memberGroupsQuery,
      variables: {
        after,
        first,
        filter: getFilter(),
      },
    });

    return result.memberGroups ?? [];
  }

  const {
    data,
    isLoading,
    isError: isDataError,
    loadedAll,
  } = useReactQueryLazyLoading(
    scroller,
    [...baseKey, 'member-group-picker', { filter: getFilter() }],
    getMemberGroups,
    sectionSize,
    { enabled: toggle.isOpen }
  );

  function getFilter(): MemberGroupFilter {
    const filter: MemberGroupFilter = { deletedOn: { isNull: true } };

    if (filterByPermission && excludeIds) {
      throw new Error('Cannot use filterByPermission and excludeIds at the same time');
    }

    if (filterByPermission && activePosition?.scopeByGroup) {
      filter.id = getGraphQlEqualComparison(activeMember.memberGroupId) ?? null;
    } else if (excludeIds && excludeIds.length > 0) {
      filter.id = { doesNotContain: excludeIds };
    }

    return filter;
  }

  const classes = classNames('employee-picker', className);

  return (
    <GroupPicker
      currentGroup={selectedGroup ?? null}
      className={classes}
      setScrollerRef={setScrollerRef}
      loadedAll={loadedAll}
      dataError={isDataError}
      data={data}
      isLoading={isLoading}
      onOpen={executeFunctionsCurried(toggle.open, onOpen)}
      onClose={executeFunctionsCurried(toggle.close, onClose)}
      value={value}
      {...pickerProps}
    />
  );
}

export default EmployeeGroupPicker;
